import React, { useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ControlledInput } from 'src/components/ui/Input/Input';
//  import Select from 'src/components/ui/Select/Select';
import WarningBox, { WarningBoxColorType } from 'src/components/ui/WarningBox/WarningBox';
import ConfirmationModal from 'src/components/modals/ConfirmationModal/ConfirmationModal';
import { Control, FieldErrors, UseFormSetError, UseFormClearErrors, useWatch } from 'react-hook-form';
import { formatAmount } from 'src/utils/general';
import { useProfile } from 'src/utils/ProfileService';
import { useBarcodeValidation } from 'src/hooks/useBarcodeValidation';
import styles from './PrimaryInfo.module.scss';
import { ProductFormValues } from '../ProductFormTypes';
import Icon from '../../../ui/Icon/Icon';

export interface PrimaryInfoProps {
  editDisabled?: boolean;
  lockedUnlessDraft?: boolean;
  control: Control<ProductFormValues>;
  errors: FieldErrors<ProductFormValues>;
  setError: UseFormSetError<ProductFormValues>;
  clearErrors: UseFormClearErrors<ProductFormValues>;
  isSetProduct: boolean;
}

const PrimaryInfo: React.FC<PrimaryInfoProps> = ({
  editDisabled,
  lockedUnlessDraft,
  control,
  errors,
  setError,
  clearErrors,
  isSetProduct = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile, loading: profileLoading } = useProfile();
  const lang = profileLoading ? null : profile.lang;
  const formatter = (v: any) => formatAmount(Number(v), lang);

  /*
  const removalStrategyOptions = [
    {
      label: 'FIFO',
      value: 'FIFO',
    },
    {
      label: 'LIFO',
      value: 'LIFO',
    },
    {
      label: 'FEFO',
      value: 'FEFO',
    },
  ];

*/
  //  const categoryOptions = removalStrategyOptions;

  const isNonShippable = useWatch({ control, name: 'isNonShippable' });

  const articleNumber = useWatch({ control, name: 'articleNumber' }) || '';
  const barcode = useWatch({ control, name: 'barcode' }) || '';
  const [showNavigateConfirm, setShowNavigateConfirm] = useState(false);

  useBarcodeValidation({
    barcode,
    fieldPath: 'barcode',
    setError,
    clearErrors,
  });

  const handleArticleNumberClick = () => {
    if (!articleNumber) return;
    setShowNavigateConfirm(true);
  };

  const handleNavigateConfirm = (confirmed: boolean) => {
    setShowNavigateConfirm(false);
    if (!confirmed) return;
    const domain = JSON.stringify([['order_line.product_id.default_code', '=', articleNumber]]);
    navigate('/outbound', {
      state: { preselectedDomain: domain },
    });
  };

  return (
    <div className={classNames(styles.root)}>
      {lockedUnlessDraft && (
        <WarningBox
          title={t('general.warning')}
          copy={t('product.active_note')}
          colorType={WarningBoxColorType.Warning}
          className="mb-16"
        />
      )}
      <div className="grid grid-cols-4 items-center gap-16 mb-40">
        <div className="col-span-1 typo-text-14--bold text-faded">{t('general.name')}*</div>
        <ControlledInput
          control={control}
          name="name"
          error={!!errors.name}
          errorMessage={errors.name?.message}
          disabled={editDisabled}
          isInfoDisplay
          className="col-span-3"
          placeholder=""
          maxLength={200}
        />
        {/*
        <div className="col-span-1 typo-text-14--bold text-faded">{t('general.removal_strategy')}</div>
        <Select
          isInfoDisplay
          disabled={editDisabled}
          className="col-span-3"
          defaultValue={
            removalStrategyOptions.find(t => t.value === productData?.removal_strategy) || removalStrategyOptions[0]
          }
          options={removalStrategyOptions}
          onChange={v => updateProductData('removal_strategy', v?.value || '')}
        /> */}

        <div className="col-span-1 typo-text-14--bold text-faded">{t('general.article_number_short')}</div>
        {editDisabled ? (
          <div className="col-span-3 flex items-center gap-8">
            <ControlledInput
              control={control}
              name="articleNumber"
              error={!!errors.articleNumber}
              errorMessage={errors.articleNumber?.message}
              isInfoDisplay
              disabled
              className="flex-1"
              placeholder=""
              maxLength={50}
            />
            {!!articleNumber && (
              <button
                type="button"
                onClick={handleArticleNumberClick}
                title={t('product.show_article_outbounds')}
                aria-label={t('product.show_article_outbounds')}
                className="text-faded hover:text-full">
                <Icon name="openInNew" width={20} height={20} />
              </button>
            )}
          </div>
        ) : (
          <ControlledInput
            control={control}
            name="articleNumber"
            error={!!errors.articleNumber}
            errorMessage={errors.articleNumber?.message}
            isInfoDisplay
            className="col-span-3"
            placeholder=""
            maxLength={50}
          />
        )}
        {!isNonShippable && (
          <>
            <div className="col-span-1 typo-text-14--bold text-faded">{t('general.barcode')}</div>
            <ControlledInput
              control={control}
              disabled={editDisabled || lockedUnlessDraft}
              name="barcode"
              error={!!errors.barcode}
              errorMessage={errors.barcode?.message}
              isInfoDisplay
              className="col-span-3"
              placeholder={t('general.barcode_input')}
              maxLength={20}
            />
            {/* Set products derive their price from their components, so the
                price fields (label + input) are hidden and not required. */}
            {!isSetProduct && (
              <>
                <div className="col-span-1 typo-text-14--bold text-faded flex items-center gap-2">
                  {t('general.standard_price')}*
                  <div className="relative group">
                    <Icon name="info" width={16} height={16} className="text-faded cursor-pointer" />
                    <div
                      className="
                        absolute left-full top-1/2 -translate-y-1/2 ml-2
                        p-6 bg-color-grey-400 text-white typo-text-12 rounded
                        opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10
                        max-w-xs w-max whitespace-normal font-sans
                      ">
                      {t('product.standard_price_info')}
                    </div>
                  </div>
                </div>
                <ControlledInput
                  control={control}
                  name="standardPrice"
                  error={!!errors.standardPrice}
                  errorMessage={errors.standardPrice?.message}
                  disabled={editDisabled}
                  isInfoDisplay
                  className="col-span-3"
                  placeholder=""
                  maxLength={20}
                  formatDisplay={formatter}
                  numericLocale={lang}
                />
                <div className="col-span-1 typo-text-14--bold text-faded flex items-center gap-2">
                  {t('general.list_price')}*
                  <div className="relative group">
                    <Icon name="info" width={16} height={16} className="text-faded cursor-pointer" />
                    <div
                      className="
                        absolute left-full top-1/2 -translate-y-1/2 ml-2
                        p-6 bg-color-grey-400 text-white typo-text-12 rounded
                        opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10
                        max-w-xs w-max whitespace-normal font-sans
                      ">
                      {t('product.list_price_info')}
                    </div>
                  </div>
                </div>
                <ControlledInput
                  control={control}
                  name="listPrice"
                  error={!!errors.listPrice}
                  errorMessage={errors.listPrice?.message}
                  disabled={editDisabled}
                  isInfoDisplay
                  className="col-span-3"
                  placeholder=""
                  maxLength={20}
                  formatDisplay={formatter}
                  numericLocale={lang}
                />
              </>
            )}
          </>
        )}

        {/*
        <div className="col-span-1 typo-text-14--bold text-faded">{t('general.category')}</div>
        <Select
          isInfoDisplay
          disabled={editDisabled}
          className="col-span-3"
          defaultValue={categoryOptions.find(t => t.value === productData?.category) || categoryOptions[0]}
          options={categoryOptions}
          onChange={v => updateProductData('category', v?.value || '')}
        />
        */}
      </div>
      <ConfirmationModal
        isOpen={showNavigateConfirm}
        onClose={handleNavigateConfirm}
        title={t('general.confirm')}
        description={t('product.navigate_to_outbound_confirm')}
      />
    </div>
  );
};

export default PrimaryInfo;
